import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import ContinueListeningRail from "@/components/ContinueListeningRail";

const FALLBACK_HERO = {
  hero_image: "https://customer-assets.emergentagent.com/job_d6883ed5-a523-438e-9fd4-86d3f4ba7350/artifacts/k2tmsgtl_use.jpg",
  name: "",
  subtitle: "",
};

const CATEGORIES = [
  { key: "book", label: "Books" },
  { key: "music", label: "Music" },
  { key: "art", label: "Objects" },
  { key: "apparel", label: "Apparel" },
  { key: "stream", label: "Streams" },
  { key: "ticket", label: "Tickets" },
];

const APPAREL_SECTIONS = [
  { key: "men", label: "Men" },
  { key: "women", label: "Women" },
  { key: "children", label: "Children" },
  { key: "accessories", label: "Accessories" },
];

const ProductCard = ({ p, size = "normal" }) => {
  const tall = size === "tall";
  const wide = size === "wide";
  const isTicket = p.category === "ticket";
  const lowStock = isTicket && !p.sold_out && p.tickets_remaining != null && p.tickets_remaining <= 5 && p.tickets_remaining > 0;
  return (
    <Link
      to={`/product/${p.product_id}`}
      data-testid={`product-card-${p.product_id}`}
      className={`group relative block bg-white brut-border overflow-hidden transition-transform duration-100 hover:-translate-x-1 hover:-translate-y-1 hover:brut-shadow ${tall ? "row-span-2" : ""} ${wide ? "md:col-span-2" : ""}`}
    >
      <div className={`relative overflow-hidden bg-[#EAEAEA] ${tall ? "aspect-[3/4]" : "aspect-[4/3]"}`}>
        {p.image_url ? (
          <img src={p.image_url} alt={p.title} className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 ${p.sold_out ? "opacity-50" : ""}`} />
        ) : (
          <div className="w-full h-full grid place-items-center text-[#555]">No image</div>
        )}
        <span className="pill-label absolute top-3 left-3">{isTicket ? "ticket" : p.type}</span>
        {p.sold_out && (
          <div className="absolute inset-0 grid place-items-center">
            <div className="brut-border bg-[#1F3D2A] text-white px-5 py-2 mono uppercase tracking-[0.3em] text-sm font-bold">Sold out</div>
          </div>
        )}
        {lowStock && (
          <div className="absolute top-3 right-3 brut-border bg-[#C77E3C] text-white px-2 py-1 mono uppercase tracking-[0.2em] text-[10px] font-bold animate-pulse">
            {p.tickets_remaining} left
          </div>
        )}
      </div>
      <div className="p-4 border-t-2 border-[#1F3D2A]">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#555]">{p.category}</div>
            <h3 className="heading text-xl font-bold mt-1 leading-tight">{p.title}</h3>
            <div className="mono text-xs text-[#555] mt-1">by {p.seller_name}</div>
          </div>
          <div className="brut-border bg-[#C77E3C] text-white px-3 py-1 font-bold mono whitespace-nowrap">
            {p.price === 0 ? "FREE" : `$${p.price.toFixed(2)}`}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default function Home() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "";
  const section = params.get("section") || "";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [featured, setFeatured] = useState(FALLBACK_HERO);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (cat) params.category = cat;
    if (cat === "apparel" && section) params.apparel_section = section;
    api.get("/products", { params })
      .then(({ data }) => setProducts(data))
      .finally(() => setLoading(false));
  }, [cat, section]);

  useEffect(() => {
    api.get("/featured-artisans")
      .then(({ data }) => { if (data && data.length) setFeatured(data[0]); })
      .catch(() => {});
  }, []);

  return (
    <Layout>
      {/* HERO */}
      <section className="border-b-2 border-[#1F3D2A] relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-7">
            <div className="mono text-xs uppercase tracking-[0.3em] text-[#C77E3C]" data-testid="hero-eyebrow">/ Indie storefront — est. 2026</div>
            <h1 className="heading text-5xl sm:text-7xl lg:text-[7.5rem] font-black uppercase leading-[0.9] tracking-tighter mt-4">
              Buy work<br />from the people<br />who <span className="text-[#C77E3C]">make it.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-[#555] leading-relaxed">
              Books pressed in attics. Songs cut in basements. Vessels thrown by hand. Jubasmash is a curated marketplace for independent artisans — every product is reviewed before it goes live.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/?cat=book" data-testid="hero-shop-btn" className="btn-primary">Shop the store →</Link>
              <Link to="/sell" data-testid="hero-sell-btn" className="btn-ghost">Sell your work</Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="brut-border bg-white overflow-hidden brut-shadow-accent">
              <img src={featured.hero_image} alt={featured.name} className="w-full aspect-[4/5] object-cover" data-testid="featured-hero-img" />
            </div>
            {featured.name && (
              <div className="absolute -bottom-4 -left-4 bg-[#1F3D2A] text-white p-4 brut-border max-w-[260px]">
                <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#C77E3C]">Featured artisan</div>
                <div className="heading text-xl font-bold mt-1" data-testid="featured-name">{featured.name}</div>
                {featured.subtitle && <div className="text-xs text-white/60 mt-1">{featured.subtitle}</div>}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CONTINUE LISTENING (logged-in buyers with owned streams only) */}
      <ContinueListeningRail />

      {/* CATEGORY TABS */}
      <section className="border-b-2 border-[#1F3D2A]">
        <div className="max-w-[1400px] mx-auto px-6 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            <button data-testid="cat-all" onClick={() => setParams({})} className={`brut-border px-4 py-2 mono uppercase text-xs tracking-[0.2em] ${!cat ? "bg-[#1F3D2A] text-white" : "bg-white"}`}>All</button>
            {CATEGORIES.map((c) => (
              <button key={c.key} data-testid={`cat-${c.key}`} onClick={() => setParams({ cat: c.key })} className={`brut-border px-4 py-2 mono uppercase text-xs tracking-[0.2em] ${cat === c.key ? "bg-[#1F3D2A] text-white" : "bg-white"}`}>{c.label}</button>
            ))}
          </div>
          <div className="mono text-xs uppercase tracking-[0.2em] text-[#555]">{products.length} items</div>
        </div>
        {cat === "apparel" && (
          <div className="max-w-[1400px] mx-auto px-6 pb-6 flex flex-wrap items-center gap-2 border-t-2 border-dashed border-[#1F3D2A] pt-4">
            <span className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C] mr-2">/ Apparel</span>
            <button data-testid="sec-all" onClick={() => setParams({ cat: "apparel" })} className={`brut-border px-3 py-1 mono uppercase text-[10px] tracking-[0.2em] ${!section ? "bg-[#C77E3C] text-white" : "bg-white"}`}>All</button>
            {APPAREL_SECTIONS.map((s) => (
              <button key={s.key} data-testid={`sec-${s.key}`} onClick={() => setParams({ cat: "apparel", section: s.key })} className={`brut-border px-3 py-1 mono uppercase text-[10px] tracking-[0.2em] ${section === s.key ? "bg-[#C77E3C] text-white" : "bg-white"}`}>{s.label}</button>
            ))}
          </div>
        )}
      </section>

      {/* PRODUCT GRID */}
      <section className="max-w-[1400px] mx-auto px-6 py-12">
        {loading ? (
          <div className="grid place-items-center py-32 mono text-sm uppercase tracking-[0.2em] text-[#555]">Loading…</div>
        ) : products.length === 0 ? (
          <div className="brut-border bg-white p-12 text-center">
            <div className="heading text-3xl font-bold uppercase">Nothing here yet</div>
            <p className="text-[#555] mt-2">Try a different category or check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 auto-rows-auto gap-6" data-testid="product-grid">
            {products.map((p, idx) => (
              <ProductCard key={p.product_id} p={p} size={idx % 5 === 0 ? "wide" : "normal"} />
            ))}
          </div>
        )}
      </section>

      {/* CTA STRIP */}
      <section className="border-y-2 border-[#1F3D2A] bg-[#C77E3C] text-white">
        <div className="max-w-[1400px] mx-auto px-6 py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="mono text-xs uppercase tracking-[0.3em]">/ Sellers</div>
            <h2 className="heading text-3xl md:text-5xl font-black uppercase tracking-tight mt-2">Make something. Sell it here.</h2>
          </div>
          <Link to="/sell" data-testid="cta-sell-btn" className="brut-border bg-[#1F3D2A] text-white font-bold uppercase tracking-wider px-6 py-3 hover:bg-white hover:text-[#1F3D2A] transition-colors">Apply to sell →</Link>
        </div>
      </section>
    </Layout>
  );
}
